import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { fmtNum } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Pencil, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "./OfficerHome";

export default function RateBoard() {
  const { user } = useAuth();
  const [rates, setRates] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-6 w-6 text-[#EAB308]" />
        <h1 className="font-head text-2xl font-bold">Rate Board</h1>
      </div>
      <p className="text-sm text-[#64748B]">Rate vs IDR. {user?.role === "owner" ? "Klik edit untuk mengubah." : "Hanya owner yang dapat mengubah rate."}</p>

      <TargetMarginCard isOwner={user?.role === "owner"} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rates.map((r) => (
          <div key={r.currency} className="nnx-card p-4" data-testid={`rate-card-${r.currency}`}>
            <div className="flex items-center justify-between">
              <div className="font-head font-bold text-lg">{r.currency}<span className="text-[#64748B] text-sm">/IDR</span></div>
              {user?.role === "owner" && (
                <button data-testid={`edit-rate-${r.currency}`} onClick={() => setEdit(r)} className="p-1.5 rounded-lg border border-[#222B3D] hover:border-[#EAB308] hover:text-[#EAB308]"><Pencil className="h-3.5 w-3.5" /></button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2.5">
                <div className="text-[10px] uppercase tracking-widest text-emerald-400">Buy</div>
                <div className="mono font-bold text-lg text-emerald-400">{fmtNum(r.buy_rate)}</div>
              </div>
              <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-2.5">
                <div className="text-[10px] uppercase tracking-widest text-rose-400">Sell</div>
                <div className="mono font-bold text-lg text-rose-400">{fmtNum(r.sell_rate)}</div>
              </div>
            </div>
            <div className="mt-2 rounded-lg bg-[#EAB308]/5 border border-[#EAB308]/20 p-2">
              <div className="text-[10px] uppercase tracking-widest text-[#EAB308]">Valuasi / Cost (IDR)</div>
              <div className="mono font-bold text-[#EAB308]">{r.valuation_idr ? fmtNum(r.valuation_idr) : "-"}</div>
            </div>
            <div className="text-[10px] text-[#64748B] mt-2">
              {r.prev_sell ? `Prev sell: ${fmtNum(r.prev_sell)} · ` : ""}by {r.updated_by}
            </div>
          </div>
        ))}
      </div>

      {edit && <EditRate rate={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function EditRate({ rate, onClose, onDone }) {
  const [buy, setBuy] = useState(rate.buy_rate);
  const [sell, setSell] = useState(rate.sell_rate);
  const [valuation, setValuation] = useState(rate.valuation_idr || "");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.put(`/rates/${rate.currency}`, { buy_rate: buy, sell_rate: sell, valuation_idr: valuation });
      toast.success(`Rate ${rate.currency} diperbarui`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Modal title={`Edit Rate ${rate.currency}`} onClose={onClose} testid="edit-rate-modal">
      <div className="space-y-3">
        <div><label className="label">Buy Rate (customer)</label><input data-testid="edit-buy" inputMode="decimal" className="nnx-input mono mt-1" value={buy} onChange={(e) => setBuy(e.target.value)} /></div>
        <div><label className="label">Sell Rate (customer)</label><input data-testid="edit-sell" inputMode="decimal" className="nnx-input mono mt-1" value={sell} onChange={(e) => setSell(e.target.value)} /></div>
        <div>
          <label className="label">Valuasi / Cost / Liquidation (IDR per unit)</label>
          <input data-testid="edit-valuation" inputMode="decimal" className="nnx-input mono mt-1" value={valuation} onChange={(e) => setValuation(e.target.value)} placeholder="mis. 3850" />
          <p className="text-[11px] text-[#64748B] mt-1">Dipakai untuk hitung estimated profit, break-even & min-safe rate. Untuk MYR = harga liquidation ke money changer.</p>
        </div>
      </div>
      <button className="btn-gold w-full mt-4" data-testid="save-rate" onClick={submit} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "SIMPAN RATE"}</button>
    </Modal>
  );
}

function TargetMarginCard({ isOwner }) {
  const [val, setVal] = useState("");
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/settings/target-margin").then((r) => setVal(r.data.target_margin_pct)).catch(() => {}); }, []);
  const save = async () => {
    setLoading(true);
    try { await api.put("/settings/target-margin", { target_margin_pct: val }); toast.success("Target margin disimpan"); setEdit(false); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <div className="nnx-card p-4 flex items-center justify-between" data-testid="target-margin-card">
      <div><div className="label">Target Margin</div><div className="text-xs text-[#64748B]">Untuk menghitung Minimum Safe Rate</div></div>
      <div className="flex items-center gap-2">
        {edit ? (
          <>
            <input data-testid="target-margin-input" inputMode="decimal" className="nnx-input mono w-24 text-right" value={val} onChange={(e) => setVal(e.target.value.replace(/[^0-9.]/g, ""))} />
            <span className="text-[#EAB308]">%</span>
            <button className="btn-gold px-3 py-1.5" data-testid="save-target-margin" onClick={save} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}</button>
          </>
        ) : (
          <>
            <span className="mono font-bold text-xl text-[#EAB308]" data-testid="target-margin-value">{val || "-"}%</span>
            {isOwner && <button className="p-1.5 rounded-lg border border-[#222B3D] hover:border-[#EAB308] hover:text-[#EAB308]" data-testid="edit-target-margin" onClick={() => setEdit(true)}><Pencil className="h-3.5 w-3.5" /></button>}
          </>
        )}
      </div>
    </div>
  );
}
